/**
 * Usage Service for handling usage data operations
 * 
 * SECURITY UPDATE: April 2025
 * All direct references to backend URLs have been replaced with secure server proxies
 * to prevent exposing sensitive backend URLs in client-side code.
 */
export const useUsageService = () => {

  /**
   * Get usage data for a specific service
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const usages = async (serviceId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/usages`,
        {
          method: "POST",
          credentials: "include",
          body: { serviceId },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Get usage report content for a specific service and report path
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const usageReportContent = async (
    serviceId: string,
    reportPath: string
  ): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/usage-report-content`,
        {
          method: "POST",
          credentials: "include",
          body: { serviceId, reportPath },
          responseType: "blob",
        }
      );

      return response || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Send usage notification
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const usageNotify = async (notifyId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/usage-notify`,
        {
          method: "POST",
          credentials: "include",
          body: { notifyId },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Get usage periods for a specific MSN
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const usagePeriods = async (msn: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/usage-periods`,
        {
          method: "POST",
          credentials: "include",
          body: { msn },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Request usage data
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const usageRequest = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/usage-request`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  return {
    usages,
    usageReportContent,
    usageNotify,
    usagePeriods,
    usageRequest,
  };
};
